import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { Badge } from "react-bootstrap";
import { IoIosAddCircle, IoIosCloseCircle, IoIosRemoveCircle } from "react-icons/io";
import { RoleResponseModel } from "../../_core/_model";
import { useAppContext } from "../../context/AppContext.tsx";
import { defaultTheme } from "../../styles/themes/defaultTheme";
import DimensionFilter from "../Tools/DimensionFilter.tsx";
import TooltipButton from "../Tools/TooltipButton.tsx";
import AssignedRolesSelector from "./AssignedRolesSelector.tsx";
import EmptyAssignedRoles from "./EmptyAssignedRoles.tsx";
import RoleCard from "./RoleCard.tsx";
import RoleGroupsList from "./RoleGroupsList.tsx";
import { RoleGroup } from "./_core/_model.ts";
import { RoleModel } from "../Filters/_core/_model.ts";
import { LabelConstants } from "../../Constants.ts";

interface innerRoleTab {
    name: "available" | "assigned" | "groups"
    title: string
}
const innerRoleTabs: innerRoleTab[] = [{ name: "assigned", title: "Assigned Roles" }, { name: "available", title: "Available Roles" },
{ name: "groups", title: LabelConstants.RoleGroups }
];

const innerRoleTabsNoGroups: innerRoleTab[] = [{ name: "assigned", title: "Assigned Roles" }, { name: "available", title: "Available Roles" }];

interface request {
    assigned: RoleResponseModel[],
    updateAssigned: (roles: RoleResponseModel[], groups: RoleGroup[]) => void,
    groups: string | null,
    emptyMessage: string
}
interface KeyVal {
    from: string,
    id: number | null
}
export function RoleTabs({ assigned, updateAssigned, groups, emptyMessage }: request) {
    const [activeTab, setActiveTab] = useState<string>("assigned");
    const [selectedDimensions, setSelectedDimensions] = useState<any[]>([]);
    const [assignedRoles, setAssignedRoles] = useState<RoleResponseModel[]>([]);
    const [highlightIds, setHighlightIds] = useState<KeyVal[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedGroups, setSelectedGroups] = useState<RoleGroup[]>([]);
    const [badgeKey, setBadgeKey] = useState(0);
    const { roles, allRoleGroups } = useAppContext();

    useEffect(() => {
        if (groups != null && !groups?.isEmpty()) {
            let gIds = groups.split(",");
            let gArr = allRoleGroups.filter((g) => gIds.indexOf(g.roleGroupID.toString()) > -1);
            setSelectedGroups([...gArr]);
        }
        else if (selectedGroups.length > 0) {
            setSelectedGroups([]);
        }
    }, [groups])

    useEffect(() => {
        setAssignedRoles(assigned)
    }, [assigned])


    const filteredAssigned = assignedRoles.filter(role =>
        (selectedDimensions.length === 0 || selectedDimensions.includes(role.dataMapCode)) &&
        (role.roleName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            role.description?.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const filteredAvailable = roles.filter(role =>
        (selectedDimensions.length === 0 || selectedDimensions.includes(role.dataMapCode))
    );

    const handleDrop = (role: any) => {
        if (!assignedRoles.some(r => r.roleID === role.roleID)) {
            let aRoles = assignedRoles;
            updateAssigned([...assigned, role], selectedGroups);
            setBadgeKey(prev => prev + 1);
            //setRoleDirty(true);
        }
    };

    const handleRemove = (roleID: any) => {
        updateAssigned(assigned.filter(r => r.roleID !== roleID), selectedGroups);
        setHighlightIds(prev => prev.filter(id => id !== roleID));
        setBadgeKey(prev => prev + 1);
        //setRoleDirty(true);
    };

    const myParentGroup = (role: RoleResponseModel) => {
        return selectedGroups.find((g) => role.roleID != null && g.roleIDS.split(",").indexOf(role.roleID.toString()) > -1);
    }

    const assignedUI = () => {
        return (<motion.div
            key="asked"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
        >
            <AssignedRolesSelector
                helpLabel=""
                importButton={undefined}
                options={[{ name: "user", title: "User", selected: false }, { name: "group", title: LabelConstants.RoleGroups, selected: false }]} exUI={undefined} onTypeChange={() => { }} replaceOlds={false}
                typeLabel="Copy Roles from" allRoles={roles} onChange={async (r, from) => {
                    let aRoles = [...assignedRoles];
                    let newHighlightIds: KeyVal[] = [];
                    for (let i = 0; i < r.length; i++) {
                        if (aRoles.findIndex((x) => x.roleID === r[i].roleID) === -1) {
                            aRoles.push(r[i]);
                            newHighlightIds.push({ from: from, id: r[i].roleID });
                            updateAssigned([...aRoles], selectedGroups);
                            await new Promise(res => setTimeout(res, 150));
                        }
                    }
                    setHighlightIds(prev => [...prev, ...newHighlightIds]);
                }} />
            {assignedRoles.length > 0 && (
                <div className="d-flex justify-content-between align-items-center my-2 px-1">
                    <small className="text-muted-theme">
                        Assigned Roles: {assignedRoles.length}
                    </small>
                    <TooltipButton title="Clear all assigned roles">
                        <motion.button
                            className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1  j_g_rt_cl"
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                                updateAssigned([], selectedGroups);
                                setHighlightIds([]);
                            }}
                        >
                            <IoIosRemoveCircle />
                            <span>Clear All</span>
                        </motion.button>
                    </TooltipButton>
                </div>
            )}
            {
                filteredAssigned.length === 0 ?
                    <EmptyAssignedRoles msg={emptyMessage} onAssignClick={() => {
                        setActiveTab("available");
                    }} /> :
                    <AnimatePresence>
                        {filteredAssigned.map(role => (
                            <motion.div
                                key={role.roleID}
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{
                                    opacity: 1,
                                    scale: 1,
                                    boxShadow: (highlightIds.findIndex((x) => x.id == role.roleID) > -1) ? '0 0 8px rgba(72, 180, 97, 0.6)' : 'none'
                                }}
                                exit={{ opacity: 0, scale: 0.85 }}
                                transition={{ duration: 0.25 }}
                            >
                                <div className="position-relative">
                                    <RoleCard {...{
                                        role: role,
                                        exButton:
                                            <div className="d-flex flex-row align-items-center gap-2 j_rt_rem" >
                                                {(highlightIds.findIndex((x) => x.id == role.roleID) > -1) && (
                                                    <Badge bg="success" pill className="" style={{ zIndex: 1 }}>
                                                        {
                                                            highlightIds.find((x) => x.id == role.roleID)?.from
                                                        }
                                                    </Badge>
                                                )}
                                                {
                                                    typeof myParentGroup(role) != 'undefined' ?
                                                        <TooltipButton title={`Cannot remove this Role, it is a part of assigned ${myParentGroup(role)?.roleGroupName} Group.`}>
                                                            <button className="btn btn-link p-0">
                                                                <IoIosCloseCircle size={24} color="gray" />
                                                            </button>
                                                        </TooltipButton> :
                                                        <TooltipButton title="Unassign this Role">
                                                            <button onClick={() => handleRemove(role.roleID)} className="btn btn-link p-0">
                                                                <IoIosCloseCircle size={24} color="red" />
                                                            </button>
                                                        </TooltipButton>
                                                }
                                            </div>
                                    }} />
                                </div>
                            </motion.div>
                        ))}
                    </AnimatePresence>
            }
        </motion.div>)
    }

    const availableUI = () => {
        return (
            <motion.div
                key="available"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.3 }}
                className="mb-6"
            >
                {filteredAvailable.map(role => (
                    <RoleCard key={role.roleID} {...{
                        role: role,
                        exButton:
                            <>
                                {filteredAssigned.find(x => x.roleName === role.roleName) ? (
                                    <TooltipButton title="Remove">
                                        <button onClick={() => handleRemove(role.roleID)} className="btn btn-link p-0">
                                            <IoIosRemoveCircle size={24} color="orange" />
                                        </button>
                                    </TooltipButton>
                                ) : (
                                    <TooltipButton title="Add">
                                        <button onClick={() => handleDrop(role)} className="btn btn-link p-0">
                                            <IoIosAddCircle size={24} color="green" />
                                        </button>
                                    </TooltipButton>
                                )}
                            </>
                    }} />
                ))}
            </motion.div>
        )
    }

    const getFilterUI = () => {
        return (
            <div className="d-flex flex-column gap-2 mb-3">
                <input
                    className="form-control j_srch"
                    placeholder="Search roles by name"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                />
                <div className="j_fil">
                    {/* 🔥 Pass assignedRoles here */}
                    <DimensionFilter
                        needLabel={true}
                        needWarningAndCount={true}
                        searched={setSelectedDimensions}
                        assignedRoles={assignedRoles}
                    />
                </div>
            </div>
        )
    }

    const rolesTabBody = () => {
        return (
            <div className="d-flex flex-column">
                {getFilterUI()}
                <div className="d-flex flex-row border-bottom mb-4">
                    {(groups == null ? innerRoleTabsNoGroups : innerRoleTabs).map(tab => (
                        <button
                            key={tab.name}
                            onClick={() => setActiveTab(tab.name)}
                            className={`j_u_rT_${tab.name} bg-theme-card px-3 py-2 position-relative`}
                            style={{
                                borderWidth: "0px 0px 4px 0px",
                                borderBottom: `4px solid ${activeTab === tab.name ? defaultTheme.colors.primary : 'transparent'}`,
                                fontWeight: activeTab === tab.name ? 'bold' : 'normal',
                                color: activeTab === tab.name ? defaultTheme.colors.primary : 'gray'
                            }}
                        >
                            {tab.title}
                            <motion.span
                                key={`${tab}-${badgeKey}`}
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1.2, opacity: 1 }}
                                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                className="position-absolute top-0 start-100 translate-middle badge rounded-circle bg-info"
                                style={{ fontSize: '0.7rem', zIndex: 2 }}
                            >
                                {tab.name == 'available'
                                    ? filteredAvailable.length - filteredAssigned.length
                                    : tab.name == 'assigned' ?
                                        filteredAssigned.length : (groups == "") ? 0 : groups?.split(",").length}
                            </motion.span>
                        </button>
                    ))}
                </div>
                {
                    activeTab == "assigned" ?
                        assignedUI() :
                        activeTab == "available" ?
                            availableUI() :
                            <RoleGroupsList
                                roleGroupIDs={groups != null ? groups.split(",") : null}
                                onInit={(g) => {
                                    setSelectedGroups(g);
                                }}
                                onChange={(selectedGroups) => {
                                    let aRoles = [...assignedRoles];
                                    let newHighlightIds: KeyVal[] = [];
                                    for (var gIndx in selectedGroups) {
                                        let cGroup = selectedGroups[gIndx];
                                        let gArr = cGroup.roleIDS.split(",");
                                        let r = roles.filter((x) => x.roleID != null && gArr.indexOf(x.roleID.toString()) > -1);
                                        for (let i = 0; i < r.length; i++) {
                                            if (aRoles.findIndex((x) => x.roleID === r[i].roleID) === -1) {
                                                aRoles.push(r[i]);
                                            }
                                        }
                                    }
                                    updateAssigned([...aRoles], selectedGroups);
                                }}
                            />
                }
            </div>
        )
    }

    return (
        rolesTabBody()
    )
}

export default RoleTabs;